import { Component, DestroyRef, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { ToastService, ToastType } from '@app/components/toast';
import { AdminTenantService } from '@app/services/admin-tenant/admin-tenant.service';
import {
  ChallengeService,
  CreateChallengePayload,
} from '@app/services/challenge/challenge.service';
import { Logger } from '@app/services/logger/logger.service';
import { SaveChangesService } from '@app/services/save-changes/save-changes.service';
import { TranslatorService } from '@app/services/translator/translator.service';
import { EMPTY, catchError } from 'rxjs';
import { SaveChangesConfirmationComponent } from '../../../../shared/components/save-changes-confirmation/save-changes-confirmation.component';
import {
  StepChallengeFormComponent,
  StepChallengeFormValue,
} from '../step-challenge-form/step-challenge-form.component';

@Component({
  selector: 'app-step-challenge-create',
  templateUrl: './step-challenge-create.component.html',
  standalone: true,
  imports: [StepChallengeFormComponent, SaveChangesConfirmationComponent],
})
export class StepChallengeCreateComponent {
  private readonly router = inject(Router);
  private readonly challengeService = inject(ChallengeService);
  private readonly tenantService = inject(AdminTenantService);
  private readonly toastService = inject(ToastService);
  private readonly translatorService = inject(TranslatorService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly logger = inject(Logger);
  private readonly saveChanges = inject(SaveChangesService);

  isSaving = signal(false);
  readonly goBackNavigate = ['challenges', 'select'];

  onSave(value: StepChallengeFormValue): void {
    const tenantId = this.tenantService.getTenantId();
    if (!tenantId) {
      this.logger.error('Missing tenant ID');
      this.toastService.add(
        this.translatorService.translate('challenges.challengeCouldNotBeSaved'),
        ToastType.ERROR
      );
      return;
    }

    this.isSaving.set(true);

    const dto: CreateChallengePayload = {
      type: 'dailySteps',
      name: value.title,
      description: value.description,
      goal: value.goal,
      startDate: value.startDate,
      endDate: value.endDate,
      enrollmentStartDate: value.enrollmentStartDate,
      access: [tenantId],
      author: tenantId,
      joinInProgress: value.joinInProgress,
      teamAssignmentType: value.teamAssignmentType,
      teamAssignmentValue: value.teamAssignmentValue,
    };

    this.challengeService
      .createChallenge(dto)
      .pipe(
        catchError((err) => {
          this.logger.error('Error creating challenge');
          this.logger.error(JSON.stringify(err, null, 2));
          this.toastService.add(
            this.translatorService.translate(
              'challenges.challengeCouldNotBeCreated'
            ),
            ToastType.ERROR
          );
          this.isSaving.set(false);
          return EMPTY;
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        this.toastService.add(
          this.translatorService.translate(
            'challenges.challengeCreatedSuccessfully'
          )
        );
        this.isSaving.set(false);
        this.saveChanges.setHasChanges(false);
        this.router.navigate(['challenges']);
      });
  }

  goBack(): void {
    this.router.navigate(this.goBackNavigate);
  }
}
